<!-- eslint-disable vue/no-mutating-props -->
<template>
  <form :class="$style.form">
    <PasswordRequirementsList
      v-if="passwordRequirementItems.length"
      :items="passwordRequirementItems"
    />
    <div
      :class="$style.passwordFieldContainer"
    >
      <LoginTextField
        label="Password"
        name="password"
        placeholder="Password"
        :type="inputType"
        v-model="form.password"
        vvName="password"
        :rules="passwordFieldValidation"
        :submitOnEnter="submit"
      />
      <IconButton
        :icon="icon"
        ariaLabel="Toggle Password Visibility"
        @click="togglePasswordVisibility('password')"
        :class="$style.passwordVisibilityToggle"
      />
    </div>
    <div
      :class="$style.passwordFieldContainer"
    >
      <LoginTextField
        label="Confirm Password"
        name="confirm password"
        placeholder="Confirm Password"
        :type="confirmInputType"
        v-model="form.confirm"
        vvName="confirm password"
        :rules="confirmPasswordFieldValidation"
        :submitOnEnter="submit"
      />
      <IconButton
        :icon="confirmIcon"
        ariaLabel="Toggle Password Visibility"
        @click="togglePasswordVisibility('confirm')"
        :class="$style.passwordVisibilityToggle"
      />
    </div>
    <div
      :class="$style.termsOfUse"
      v-if="showTermsOfService"
    >
      <div :class="$style.terms">
        <input
          :class="$style.licenseInput"
          data-vv-name="Terms of Service"
          name="Terms of Service"
          required
          type="checkbox"
          v-validate="'required'"
          v-model="form.license"
        >
        <span>
          I agree to the
          <a
            href="https://jumpcloud.com/legal"
            rel="noopener noreferrer"
            target="_blank"
            :class="$style.resetPasswordLink"
          >Terms of Service</a>
        </span>
        <div
          :class="$style.errorDisplay"
          v-if="errors.has('Terms of Service')"
        >
          <i :class="$style.errorDisplayPointer" />
          <i :class="['jc-actions-alert', $style.removeIcon]" />
        </div>
      </div>
    </div>
    <LoginActionButton
      :action="submit"
      :isLoading="isLoading"
      :disabled="isSubmitDisabled"
    >
      Set Password
    </LoginActionButton>
  </form>
</template>

<script>
import {
  buildPasswordRequirementContextFromPolicy,
  getEvaluatedPasswordRequirements,
  isPasswordSatisfyingClientCheckableRequirements,
} from 'client/util/passwordPolicyRequirements';
import { extend, isEqual } from 'lodash';
import { featureFlags } from 'client/stores/FeatureFlagsModule';
import { mapState } from 'vuex';
import IconButton from 'client/components/IconButton.vue';
import LoginActionButton from 'client/components/LoginActionButton.vue';
import LoginTextField from 'client/components/LoginTextField.vue';
import PasswordRequirementsList from 'client/components/PasswordRequirementsList.vue';

/**
 * This form has the ability to v-model a form object with the following keys:
 * password, confirm, license
 * It can modify the license field based on whether or not the clientType is an admin or not.
 */
export default {
  name: 'ResetPassword',

  inject: ['$validator'],

  props: {
    clientType: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    submit: {
      type: Function,
      required: true,
    },
    passwordPolicy: {
      type: Object,
      default: null,
    },
    formEmail: {
      type: String,
      default: '',
    },
  },
  components: {
    IconButton,
    LoginActionButton,
    LoginTextField,
    PasswordRequirementsList,
  },

  data() {
    return {
      password: {
        visible: false,
        timer: null,
      },
      confirm: {
        visible: false,
        timer: null,
      },
    };
  },

  computed: {
    ...mapState('FeatureFlagsModule', {
      betaFeatures: 'betaFeatures',
    }),

    hasDisallowNameInPassword() {
      const policy = this.passwordPolicy;
      // Reset-key policy API is org-accurate; do not rely on LD alone (no org id on this page).
      if (policy?.disallowNameSubstring || policy?.enableNameConsecutiveChars) {
        return true;
      }
      return Boolean(this.betaFeatures?.[featureFlags.disallowNameInPassword]);
    },

    passwordRequirementContext() {
      return buildPasswordRequirementContextFromPolicy(this.passwordPolicy, {
        formEmail: this.formEmail || this.form.email || '',
        hasDisallowNameInPassword: this.hasDisallowNameInPassword,
      });
    },

    inputType() {
      return this.password.visible ? 'text' : 'password';
    },

    icon() {
      return this.password.visible ? 'viewableOff' : 'viewable';
    },

    confirmInputType() {
      return this.confirm.visible ? 'text' : 'password';
    },

    confirmIcon() {
      return this.confirm.visible ? 'viewableOff' : 'viewable';
    },

    showTermsOfService() {
      return this.clientType === 'admin';
    },
    passwordRequirementItems() {
      return getEvaluatedPasswordRequirements(
        this.passwordPolicy,
        this.form.password || '',
        this.passwordRequirementContext,
      );
    },
    satisfiesClientCheckableRequirements() {
      if (!this.passwordPolicy) {
        return true;
      }

      return isPasswordSatisfyingClientCheckableRequirements(
        this.passwordPolicy,
        this.form.password || '',
        this.passwordRequirementContext,
      );
    },
    isSubmitDisabled() {
      if (this.disabled || this.isLoading) {
        return true;
      }

      if (!this.form.password || !this.form.confirm) {
        return true;
      }

      if (this.form.password !== this.form.confirm) {
        return true;
      }

      if (this.showTermsOfService && !this.form.license) {
        return true;
      }

      return !this.satisfiesClientCheckableRequirements;
    },
    passwordFieldValidation() {
      const adminValidations = {};
      extend(adminValidations, this.adminFieldValidations);

      return this.clientType === 'admin' ? adminValidations : this.userFieldValidations;
    },

    confirmPasswordFieldValidation() {
      const adminValidations = { matchPassword: true };
      extend(adminValidations, this.adminFieldValidations);

      return this.clientType === 'admin' ? adminValidations : this.userFieldValidations;
    },

    adminFieldValidations() {
      return {
        required: true,
        min: 12,
        max: 64,
        noEmailPassword: true,
        noUserName: true,
        noWhitespace: true,
      };
    },

    userFieldValidations() {
      return {
        required: true,
        max: 64,
        matchPassword: true,
      };
    },
  },

  methods: {
    togglePasswordVisibility(field) {
      if (!['password', 'confirm'].includes(field)) {
        return;
      }
      this[field].visible = !this[field].visible;
      clearTimeout(this[field].timer);
      this[field].timer = setTimeout(() => {
        this[field].visible = false;
      }, 4000);
    },
  },

  beforeMount() {
    this.$validator.extend('matchPassword', {
      getMessage: 'The password confirmation does not match.',
      validate: (value) => isEqual(value, this.form.password),
    });
    this.$validator.extend('noEmailPassword', {
      getMessage: 'Password may not contain your email address',
      validate: (value) => !value.toLowerCase().includes(this.form.email.toLowerCase()),
    });

    this.$validator.extend('noUserName', {
      getMessage: 'Password may not contain your username',
      validate: (value) => !value.toLowerCase().includes(this.form.email.split('@')[0].toLowerCase()),
    });

    this.$validator.extend('noWhitespace', {
      getMessage: 'Password cannot start or end with whitespace',
      validate: (value) => isEqual(value, value.trim()),
    });
  },
};
</script>

<style module>
@import '../../css/login-components.css';

.form {
  width: 100%;
}

.termsOfUse {
  padding: 20px 0;
  text-align: center;
}

.terms {
  display: inline-block;
  font-size: 13px;
  position: relative;
}

.licenseInput {
  margin-right: 10px;
  position: relative;
}

.errorDisplay {
  font-size: 18px;
  padding: 10px 10px 8px;
}

.errorDisplayPointer {
  left: 0;
}

.passwordFieldContainer {
  align-items: flex-end;
  display: flex;
  gap: 0.5rem;
}

.passwordVisibilityToggle {
  margin-bottom: 1.5rem;
}
</style>
